import {
  pgTable,
  text,
  timestamp,
  boolean,
  index,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";

/**
 * Better Auth core tables.
 * These are generated based on Better Auth's schema requirements
 * with the plugins: jwt, bearer, twoFactor, magicLink, organization, admin, openAPI, genericOAuth.
 *
 * Run `npx @better-auth/cli generate` to regenerate if plugins change.
 */

export const user = pgTable("user", {
  id: text("id").primaryKey(),
  name: text("name").notNull(),
  email: text("email").notNull().unique(),
  emailVerified: boolean("email_verified").notNull().default(false),
  image: text("image"),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  updatedAt: timestamp("updated_at").notNull().defaultNow(),
  userRole: text("user_role").notNull().default("free"),
  twoFactorEnabled: boolean("two_factor_enabled"),
  role: text("role"),
  banned: boolean("banned"),
  banReason: text("ban_reason"),
  banExpires: timestamp("ban_expires"),
});

// Security note: session tokens, OAuth tokens, and TOTP seeds are stored as text fields.
// Better Auth manages application-layer token security (cryptographic randomness, rotation,
// expiry). The schema matches Better Auth's CLI-generated migration output — do not modify
// column types.
export const session = pgTable(
  "session",
  {
    id: text("id").primaryKey(),
    expiresAt: timestamp("expires_at").notNull(),
    token: text("token").notNull().unique(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
    activeOrganizationId: text("active_organization_id"),
    impersonatedBy: text("impersonated_by"),
  },
  (table) => [index("idx_session_user_id").on(table.userId)]
);

export const account = pgTable(
  "account",
  {
    id: text("id").primaryKey(),
    accountId: text("account_id").notNull(),
    providerId: text("provider_id").notNull(),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    accessToken: text("access_token"),
    refreshToken: text("refresh_token"),
    idToken: text("id_token"),
    accessTokenExpiresAt: timestamp("access_token_expires_at"),
    refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
    scope: text("scope"),
    password: text("password"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    updatedAt: timestamp("updated_at").notNull().defaultNow(),
  },
  (table) => [index("idx_account_user_id").on(table.userId)]
);

export const verification = pgTable("verification", {
  id: text("id").primaryKey(),
  identifier: text("identifier").notNull(),
  value: text("value").notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Two-Factor Authentication tables
export const twoFactor = pgTable("two_factor", {
  id: text("id").primaryKey(),
  secret: text("secret").notNull(),
  backupCodes: text("backup_codes").notNull(),
  userId: text("user_id")
    .notNull()
    .references(() => user.id, { onDelete: "cascade" }),
});

// Organization tables
//
// AG-008: `parentOrganizationId` is a nullable self-reference enabling
// recursive sub-agency hierarchies (white-label resellers, agency networks).
// Better Auth's organization plugin owns the `organization` table and emits
// `id` as `text` (Better Auth ID strategy), so the self-reference uses the
// same column type. ON DELETE SET NULL keeps descendants alive when a parent
// is deleted — orphaned children become roots rather than cascade-deleted
// (preserves customer data; deletion of an entire agency tree requires an
// explicit cascade in application code, audited via CQRS).
//
// White-label inheritance model: child organizations inherit brand assets,
// pricing tiers, and feature flags from their nearest ancestor that defines
// them, unless the child sets an explicit override. Resolution walks parent
// chain via the recursive CTE in `getDescendants()` (inverted), so the
// inheritance lookup is a single round-trip. Document any new inheritable
// field on the child entity with `// inherits-from-parent` so the resolver
// stays discoverable.
export const organization = pgTable(
  "organization",
  {
    id: text("id").primaryKey(),
    name: text("name").notNull(),
    slug: text("slug").notNull().unique(),
    logo: text("logo"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    metadata: text("metadata"),
    parentOrganizationId: text("parent_organization_id").references(
      (): AnyPgColumn => organization.id,
      { onDelete: "set null" },
    ),
  },
  (table) => [
    index("idx_organization_parent").on(table.parentOrganizationId),
  ],
);

export const member = pgTable(
  "member",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
    role: text("role").notNull().default("member"),
    createdAt: timestamp("created_at").notNull().defaultNow(),
  },
  (table) => [
    index("idx_member_user_id").on(table.userId),
    index("idx_member_org_id").on(table.organizationId),
  ]
);

export const invitation = pgTable(
  "invitation",
  {
    id: text("id").primaryKey(),
    organizationId: text("organization_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    email: text("email").notNull(),
    role: text("role"),
    status: text("status").notNull().default("pending"),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").notNull().defaultNow(),
    inviterId: text("inviter_id")
      .notNull()
      .references(() => user.id, { onDelete: "cascade" }),
  },
  (table) => [index("idx_invitation_org_id").on(table.organizationId)]
);

// JWT / JWKS table
export const jwks = pgTable("jwks", {
  id: text("id").primaryKey(),
  publicKey: text("public_key").notNull(),
  privateKey: text("private_key").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  expiresAt: timestamp("expires_at"),
});
